import { useEffect, useRef } from 'react'

import { useNavigate } from '@tanstack/react-router'

import { useBottomSheetContext } from '@/shared/contexts/BottomSheetContext'
import { useRewardsStore } from '@/shared/store/useRewardsStore'

import { DigitalProductStoreBottomSheet } from '../BottomSheet'
import {
  clearStoreSheetSearch,
  isStoreSheetSearchActive,
  type StoreSheetSearch
} from '../lib/storeSheetSearch'

type UseStoreSheetFromUrlOptions = {
  sheetSearch: StoreSheetSearch
}

/**
 * Sincroniza `/lojinha?reward=&item=` com o bottom sheet do item.
 * Ver `design-system/patterns/url-driven-sheets.md`.
 */
export const useStoreSheetFromUrl = ({
  sheetSearch
}: UseStoreSheetFromUrlOptions) => {
  const navigate = useNavigate()
  const { rewardItems } = useRewardsStore()
  const { showBottomSheet, hideBottomSheet } = useBottomSheetContext() as {
    showBottomSheet: (props: Record<string, unknown>) => void
    hideBottomSheet: () => void
  }
  const openedKeyRef = useRef<string | null>(null)

  const clearSheetSearch = () => {
    void navigate({
      to: '/lojinha',
      replace: true,
      search: clearStoreSheetSearch() as never
    })
  }

  useEffect(() => {
    if (!isStoreSheetSearchActive(sheetSearch)) {
      if (openedKeyRef.current !== null) {
        hideBottomSheet()
        openedKeyRef.current = null
      }
      return
    }

    const { reward, item } = sheetSearch
    const sheetKey = `${reward}-${item}`

    const rewardItem = rewardItems.find(
      candidate =>
        candidate.id === item && candidate.reward_id === reward
    )

    if (!rewardItem) return

    if (openedKeyRef.current === sheetKey) return

    openedKeyRef.current = sheetKey
    showBottomSheet({
      component: DigitalProductStoreBottomSheet,
      rewardId: reward,
      rewardItemId: item,
      onHide: clearSheetSearch
    })
  }, [sheetSearch, rewardItems, showBottomSheet, hideBottomSheet, navigate])
}
